<template>
    <div class="donation-allocation-picker">
        <h3 class="section-title mb-2">
            <v-icon color="primary" class="mr-2">mdi-hand-heart</v-icon>
            Where would you like your donation to go?
        </h3>
        <p class="section-subtitle mb-4">
            Optional - defaults to general fund if not specified
        </p>

        <v-radio-group
            v-model="allocationMode"
            column
            hide-details
            class="allocation-mode-group mt-0"
        >
            <v-radio value="general" class="allocation-radio">
                <template v-slot:label>
                    <span class="radio-label">Let the student decide</span>
                </template>
            </v-radio>
            <v-radio value="specific" class="allocation-radio">
                <template v-slot:label>
                    <span class="radio-label">I want to choose specific items</span>
                </template>
            </v-radio>
        </v-radio-group>

        <v-expand-transition>
            <div v-if="allocationMode === 'specific'" class="allocations-container mt-4">
                <v-card elevation="0" outlined class="pa-4">
                    <div
                        v-for="item in items"
                        :key="item.id"
                        class="allocation-item mb-3"
                    >
                        <v-row align="center" no-gutters>
                            <v-col cols="8">
                                <div class="d-flex align-center">
                                    <v-icon small class="mr-2" color="primary">
                                        {{ getCategoryIcon(item.category) }}
                                    </v-icon>
                                    <div>
                                        <span class="item-name">{{ item.name }}</span>
                                        <div class="item-needs" :class="{ 'success--text': isFullyFunded(item) }">
                                            <template v-if="isFullyFunded(item)">
                                                <v-icon x-small color="success" class="mr-1">mdi-check-circle</v-icon>
                                                Fully funded
                                            </template>
                                            <template v-else>
                                                Needs ${{ formatAmount(getRemainingAmount(item)) }}
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="4">
                                <v-text-field
                                    v-model.number="localAllocations[item.id]"
                                    outlined
                                    dense
                                    hide-details
                                    type="number"
                                    min="0"
                                    :max="donationAmount"
                                    prefix="$"
                                    :disabled="isFullyFunded(item)"
                                    placeholder="0"
                                    class="allocation-input"
                                    @input="onAllocationChange"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </div>

                    <v-divider class="my-3"></v-divider>

                    <v-row no-gutters class="allocation-summary">
                        <v-col cols="12" class="d-flex justify-space-between">
                            <div>
                                <span class="summary-label">Allocated:</span>
                                <span class="summary-value" :class="{ 'error--text': totalAllocated > donationAmount }">
                                    ${{ formatAmount(totalAllocated) }}
                                </span>
                                <span class="summary-separator">of</span>
                                <span class="summary-value">${{ formatAmount(donationAmount) }}</span>
                            </div>
                            <div v-if="unallocatedAmount > 0">
                                <span class="summary-label">Unallocated:</span>
                                <span class="summary-value grey--text">${{ formatAmount(unallocatedAmount) }}</span>
                            </div>
                        </v-col>
                    </v-row>

                    <v-alert
                        v-if="totalAllocated > donationAmount"
                        type="error"
                        dense
                        text
                        class="mt-3 mb-0"
                    >
                        Total allocation exceeds your donation amount.
                    </v-alert>

                    <v-alert
                        v-else-if="unallocatedAmount > 0"
                        type="info"
                        dense
                        text
                        class="mt-3 mb-0"
                    >
                        ${{ formatAmount(unallocatedAmount) }} will go to the general fund.
                    </v-alert>
                </v-card>
            </div>
        </v-expand-transition>
    </div>
</template>

<script>
const CATEGORY_ICONS = {
    tuition: 'mdi-school',
    books: 'mdi-book-open-variant',
    rent: 'mdi-home',
    furniture: 'mdi-sofa',
    gas: 'mdi-gas-station',
    food: 'mdi-food',
    custom: 'mdi-pencil',
    other: 'mdi-dots-horizontal'
}

export default {
    name: 'DonationAllocationPicker',
    props: {
        items: {
            type: Array,
            default: () => []
        },
        donationAmount: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            allocationMode: 'general',
            localAllocations: {}
        }
    },
    computed: {
        totalAllocated() {
            return Object.values(this.localAllocations).reduce((sum, amount) => {
                return sum + (parseFloat(amount) || 0)
            }, 0)
        },
        unallocatedAmount() {
            return Math.max(0, this.donationAmount - this.totalAllocated)
        },
        allocationsArray() {
            if (this.allocationMode !== 'specific') return []
            return Object.entries(this.localAllocations)
                .filter(([, amount]) => amount && parseFloat(amount) > 0)
                .map(([itemId, amount]) => ({
                    campaign_item_id: parseInt(itemId),
                    amount: Math.round(parseFloat(amount) * 100)
                }))
        }
    },
    watch: {
        items: {
            handler(newItems) {
                this.initializeAllocations(newItems)
            },
            immediate: true
        },
        donationAmount() {
            this.onAllocationChange()
        }
    },
    methods: {
        getCategoryIcon(category) {
            return CATEGORY_ICONS[category] || CATEGORY_ICONS.other
        },
        initializeAllocations(items) {
            const newAllocations = {}
            items.forEach(item => {
                newAllocations[item.id] = this.localAllocations[item.id] || null
            })
            this.localAllocations = newAllocations
        },
        getRemainingAmount(item) {
            const raised = parseFloat(item.raised_amount) || 0
            const goal = parseFloat(item.goal_amount) || 0
            return Math.max(0, goal - raised)
        },
        isFullyFunded(item) {
            return this.getRemainingAmount(item) <= 0
        },
        formatAmount(amount) {
            return (parseFloat(amount) || 0).toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            })
        },
        onAllocationChange() {
            this.$emit('update:allocations', this.allocationsArray)
            this.$emit('allocationsChanged', this.allocationsArray, this.totalAllocated)
        },
        getAllocations() {
            return this.allocationsArray
        },
        isValid() {
            return this.totalAllocated <= this.donationAmount
        }
    }
}
</script>

<style scoped>
.section-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    display: flex;
    align-items: center;
}

.section-subtitle {
    font-size: 14px;
    color: #666;
    font-style: italic;
}

.allocation-mode-group >>> .v-radio {
    margin-bottom: 8px;
}

.allocation-radio {
    padding: 12px 16px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    margin-bottom: 8px !important;
}

.allocation-radio.v-item--active {
    border-color: #1976d2;
    background: rgba(25, 118, 210, 0.04);
}

.radio-label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.allocations-container .v-card {
    border-radius: 10px !important;
    background: #fafbfc;
}

.allocation-item {
    padding: 12px;
    background: white;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.item-name {
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.item-needs {
    font-size: 12px;
    color: #666;
}

.allocation-input {
    max-width: 120px;
}

.allocation-summary {
    font-size: 14px;
}

.summary-label {
    color: #666;
    margin-right: 4px;
}

.summary-value {
    font-weight: 600;
    color: #333;
}

.summary-separator {
    color: #999;
    margin: 0 4px;
}
</style>
